import { useEffect, useState } from 'react'
import { queueSummary } from '../sync/db'
import { CHANNEL_NAME, drain } from '../sync/engine'
import type { QueueSummary } from '../sync/types'

/**
 * The persistent, visible queue state (v3 section 10 - non-negotiable).
 * Always rendered, at the top, in large type. It reads IndexedDB directly,
 * so it is right even when the page just reloaded offline.
 */
export function QueueIndicator() {
  const [summary, setSummary] = useState<QueueSummary | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    const refresh = () => {
      queueSummary().then((s) => {
        if (alive) setSummary(s)
      })
    }
    refresh()
    const timer = window.setInterval(refresh, 2000)
    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
      channel.onmessage = refresh
    } catch {
      /* no BroadcastChannel: the timer covers it */
    }
    return () => {
      alive = false
      window.clearInterval(timer)
      channel?.close()
    }
  }, [])

  const retryNow = async () => {
    setBusy(true)
    try {
      await drain({ source: 'manual' })
    } finally {
      setBusy(false)
      setSummary(await queueSummary())
    }
  }

  if (!summary) return <div style={bar('#6b7280')}>Checking upload queue…</div>

  if (summary.pendingStops === 0) {
    return <div style={bar('#059669')}>All stops uploaded{summary.synced ? ` · ${summary.synced} synced` : ''}</div>
  }

  const label = `${summary.pendingStops} stop${summary.pendingStops === 1 ? '' : 's'} pending upload`
  const detail =
    summary.syncing > 0
      ? 'Uploading…'
      : summary.failed > 0
        ? `Waiting to retry${summary.nextAttemptAt ? ` at ${new Date(summary.nextAttemptAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}` : ''}${summary.lastError ? ` · ${summary.lastError}` : ''}`
        : 'Queued'
  return (
    <div style={bar(summary.failed > 0 ? '#b45309' : '#2563eb')}>
      <div style={{ fontSize: 22, fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 14, opacity: 0.95 }}>
        {detail}
        {summary.pendingPhotos > 0 ? ` · ${summary.pendingPhotos} photo${summary.pendingPhotos === 1 ? '' : 's'}` : ''}
      </div>
      <button onClick={retryNow} disabled={busy} style={btn}>
        {busy ? 'Trying…' : 'Try now'}
      </button>
    </div>
  )
}

const bar = (bg: string): React.CSSProperties => ({
  position: 'sticky',
  top: 0,
  zIndex: 50,
  background: bg,
  color: '#fff',
  padding: '12px 16px',
  fontSize: 18,
  fontWeight: 700,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

const btn: React.CSSProperties = {
  alignSelf: 'flex-start',
  marginTop: 6,
  minHeight: 44,
  padding: '0 18px',
  borderRadius: 8,
  border: '2px solid rgba(255,255,255,0.8)',
  background: 'transparent',
  color: '#fff',
  fontWeight: 700,
  fontSize: 16,
}
